import { useCallback, useMemo } from 'react';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { Button, Card, FormInput, FormNumberInput, FormSelect, TradingStrategySelect } from '@/components/UI';
import { formatTimeHorizon } from '@/utils/timeHorizonGenerator.ts';
import { STAGE_FILTER_OPTIONS } from './constants';
import type { CompareFilterOptions, ComparePageState, UpdateCompareParams } from './types';

interface CompareFiltersCardProps {
    availableDurations: CompareFilterOptions['durations'];
    availableHorizonsForDuration: CompareFilterOptions['horizons'];
    pageState: ComparePageState;
    searchParams: URLSearchParams;
    updateParams: UpdateCompareParams;
}

const CompareFiltersCard = ({
    availableDurations,
    availableHorizonsForDuration,
    pageState,
    searchParams,
    updateParams
}: CompareFiltersCardProps) => {
    const {
        selectedStrategyId,
        compareMode,
        selectedDuration,
        selectedHorizonSeconds,
        search,
        optionsFilter,
        hasScorecardsFilter,
        strategyHasBacktestsFilter,
        strategyHasPortfolioBacktestsFilter,
        strategyHasPortfolioFilter,
        strategyIsCurrentChampionFilter,
        minTotalEntries,
        minTotalEntities,
        filtersCollapsed
    } = pageState;

    const toggleFiltersCollapsed = useCallback(() => {
        updateParams({ filters: filtersCollapsed ? null : 'collapsed' }, { resetPage: false });
    }, [filtersCollapsed, updateParams]);

    const activeFilterCount = useMemo(() => {
        let count = 0;
        if (search.trim()) count++;
        if (optionsFilter !== 'all') count++;
        if (hasScorecardsFilter !== 'all') count++;
        if (strategyHasBacktestsFilter !== 'all') count++;
        if (strategyHasPortfolioBacktestsFilter !== 'all') count++;
        if (strategyHasPortfolioFilter !== 'all') count++;
        if (strategyIsCurrentChampionFilter !== 'all') count++;
        if (minTotalEntries !== null) count++;
        if (minTotalEntities !== null) count++;
        if (selectedDuration !== null) count++;
        if (selectedHorizonSeconds !== null) count++;
        return count;
    }, [
        strategyHasBacktestsFilter,
        strategyHasPortfolioBacktestsFilter,
        strategyHasPortfolioFilter,
        hasScorecardsFilter,
        strategyIsCurrentChampionFilter,
        minTotalEntities,
        minTotalEntries,
        optionsFilter,
        search,
        selectedDuration,
        selectedHorizonSeconds
    ]);

    const durationOptions = useMemo(() => {
        const options = [{ value: '', label: 'All durations' }];

        return [
            ...options,
            ...availableDurations.map((duration) => ({
                value: duration,
                label: duration
            }))
        ];
    }, [availableDurations, compareMode]);

    const horizonOptions = useMemo(() => {
        const options = [{ value: '', label: 'All horizons' }];

        return [
            ...options,
            ...availableHorizonsForDuration.map((horizon) => ({
                value: horizon,
                label: formatTimeHorizon(horizon)
            }))
        ];
    }, [availableHorizonsForDuration]);

    return (
        <Card
            responsivePadding
            bodyClassName={filtersCollapsed ? 'hidden' : ''}
            className="mb-6 bg-zinc-900"
            headerActions={
                <Button size="sm" variant="outline-dark" onClick={toggleFiltersCollapsed}>
                    {filtersCollapsed ? (
                        <>
                            <FaChevronDown className="mr-2" />
                            Expand
                        </>
                    ) : (
                        <>
                            <FaChevronUp className="mr-2" />
                            Collapse
                        </>
                    )}
                </Button>
            }
            headerClassName={filtersCollapsed ? '!border-b-0' : ''}
            subtitle={
                filtersCollapsed
                    ? activeFilterCount > 0
                        ? `${activeFilterCount} active ${activeFilterCount === 1 ? 'filter' : 'filters'}`
                        : 'No filters applied'
                    : undefined
            }
            title="Filters"
        >
            <div className="mb-4 text-xs text-zinc-500">
                Promotion filters are strategy-level unless the label explicitly says &quot;Experiment&quot;.
            </div>
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                <TradingStrategySelect
                    noMargin
                    label="Strategy"
                    placeholder="All strategies"
                    value={selectedStrategyId}
                    onChange={(strategyId) =>
                        updateParams({
                            strategyId: strategyId === null ? null : String(strategyId),
                            duration: null,
                            horizonSeconds: null
                        })
                    }
                />
                <FormSelect
                    noMargin
                    label="Comparison Mode"
                    options={[
                        { value: 'fixed', label: 'All duration + horizon rows' },
                        { value: 'representative', label: 'Representative horizon' }
                    ]}
                    value={compareMode}
                    onChange={(event) =>
                        updateParams({
                            mode: event.target.value,
                            horizonSeconds: event.target.value === 'representative' ? null : searchParams.get('horizonSeconds')
                        })
                    }
                />
                <FormSelect
                    noMargin
                    label="Duration"
                    options={durationOptions}
                    value={selectedDuration ?? ''}
                    onChange={(event) =>
                        updateParams({
                            duration: event.target.value || null,
                            horizonSeconds: compareMode === 'fixed' ? null : searchParams.get('horizonSeconds')
                        })
                    }
                />
                <FormSelect
                    noMargin
                    disabled={compareMode !== 'fixed'}
                    label="Horizon"
                    options={horizonOptions}
                    value={selectedHorizonSeconds ?? ''}
                    onChange={(event) => updateParams({ horizonSeconds: event.target.value || null })}
                />
                <FormInput
                    noMargin
                    label="Search"
                    placeholder="Experiment name or ID"
                    value={search}
                    onChange={(event) => updateParams({ search: event.target.value || null })}
                />
                <FormSelect
                    noMargin
                    label="Uses Options"
                    options={[
                        { value: 'all', label: 'All experiments' },
                        { value: 'with', label: 'Options enabled' },
                        { value: 'without', label: 'No options' }
                    ]}
                    value={optionsFilter}
                    onChange={(event) => updateParams({ options: event.target.value === 'all' ? null : event.target.value })}
                />
                <FormSelect
                    noMargin
                    label="Experiment Scorecards"
                    options={STAGE_FILTER_OPTIONS}
                    value={hasScorecardsFilter}
                    onChange={(event) => updateParams({ scorecards: event.target.value === 'all' ? null : event.target.value })}
                />
                <FormSelect
                    noMargin
                    label="Strategy Backtests"
                    options={STAGE_FILTER_OPTIONS}
                    value={strategyHasBacktestsFilter}
                    onChange={(event) => updateParams({ backtests: event.target.value === 'all' ? null : event.target.value })}
                />
                <FormSelect
                    noMargin
                    label="Strategy Portfolio Backtests"
                    options={STAGE_FILTER_OPTIONS}
                    value={strategyHasPortfolioBacktestsFilter}
                    onChange={(event) => updateParams({ portfolioBacktests: event.target.value === 'all' ? null : event.target.value })}
                />
                <FormSelect
                    noMargin
                    label="In Current Portfolios"
                    options={STAGE_FILTER_OPTIONS}
                    value={strategyHasPortfolioFilter}
                    onChange={(event) => updateParams({ portfolio: event.target.value === 'all' ? null : event.target.value })}
                />
                <FormSelect
                    noMargin
                    label="Has Current Champion"
                    options={STAGE_FILTER_OPTIONS}
                    value={strategyIsCurrentChampionFilter}
                    onChange={(event) => updateParams({ champion: event.target.value === 'all' ? null : event.target.value })}
                />
                <FormNumberInput
                    integer
                    noMargin
                    label="Min Entries"
                    min={1}
                    placeholder="Any"
                    value={minTotalEntries}
                    onValueChange={(value) => updateParams({ minTotalEntries: value === null ? null : String(value) })}
                />
                <FormNumberInput
                    integer
                    noMargin
                    label="Min Entities"
                    min={1}
                    placeholder="Any"
                    value={minTotalEntities}
                    onValueChange={(value) => updateParams({ minTotalEntities: value === null ? null : String(value) })}
                />
            </div>
        </Card>
    );
};

export default CompareFiltersCard;
